/**
 * Shared types and helpers for the runtime policy screens.
 *
 * The form holds the generated upsert schema's values, plus one UI-only field (`fallback`, a select value that can
 * say "no fallback"). Nothing here hand-writes an API payload shape.
 */
import { z } from 'zod';
import type {
  ModelRuntimePolicy,
  ModelRuntimePolicyUpsert,
  ModelRuntimePolicyResolveRequest,
} from '../../../generated/api/types.gen.js';
import { zModelRuntimePolicyUpsert } from '../../../generated/api/zod.gen.js';

export type { ModelRuntimePolicy, ModelRuntimePolicyResolveRequest };

// ── Form schema ─────────────────────────────────────────────────────────────

/** The select value that means "no fallback policy" (the API takes `null`). */
export const NO_FALLBACK = 'none';

export const policyFormSchema = zModelRuntimePolicyUpsert
  .omit({ fallbackPolicyId: true })
  .extend({ fallback: z.string() });

export type PolicyFormValues = z.input<typeof policyFormSchema>;
export type PolicyFormOutput = z.output<typeof policyFormSchema>;

export const PURPOSES = ['interactive', 'background', 'evaluation', 'daemon', 'workflow', 'debug', 'bulk'] as const;
export const RISK_TIERS = ['low', 'medium', 'high', 'privileged'] as const;
export const LATENCY_CLASSES = ['realtime', 'interactive', 'batch'] as const;
export const COST_TIERS = ['free', 'low', 'medium', 'high'] as const;
export const PERMISSION_TIERS = ['observer', 'contributor', 'operator'] as const;
export const PERMISSION_MODES = [
  'provider_default',
  'read_only',
  'workspace_write',
  'full_auto',
  'bypass_permissions',
] as const;
export const SESSION_MODES = ['provider_default', 'stateless', 'reuse_session'] as const;
export const STATUSES = ['draft', 'active', 'archived'] as const;
export const SCOPES = ['org', 'system'] as const;
export const THINKING_MODES = ['model_default', 'on', 'off'] as const;

/** "workspace_write" → "Workspace write": enum values are never shown raw. */
export function humanizeEnum(value: string): string {
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Select options for an enum's values, in sentence case. */
export function enumOptions(values: readonly string[]): { value: string; label: string }[] {
  return values.map((value) => ({ value, label: humanizeEnum(value) }));
}

// ── Default / from-policy initializers ──────────────────────────────────────

export function defaultPolicyValues(): PolicyFormValues {
  return {
    scope: 'org',
    slug: '',
    name: '',
    description: '',
    modelIds: [],
    fallback: NO_FALLBACK,
    thinking: 'model_default',
    status: 'active',
    purpose: 'interactive',
    riskTier: 'medium',
    latencyClass: 'interactive',
    costTier: 'medium',
    permissionTier: 'contributor',
    tags: [],
    wallClockTimeoutMs: 600_000,
    providerRequestTimeoutMs: 300_000,
    idleTimeoutMs: 120_000,
    maxInputTokens: null,
    maxOutputTokens: 4096,
    maxConcurrentRequests: null,
    maxSystemPromptChars: 12_000,
    maxToolCalls: 12,
    maxTurns: 8,
    retryMax: 1,
    retryBackoffMs: 1000,
    retryBackoffMultiplier: 2,
    retryJitter: true,
    spendCapUsd: null,
    permissionMode: 'provider_default',
    allowedProviderTools: [],
    streamingEnabled: true,
    sessionMode: 'provider_default',
    promptCachingEnabled: true,
    providerExtensions: {},
  };
}

export function policyToFormValues(policy: ModelRuntimePolicy): PolicyFormValues {
  return {
    scope: policy.scope,
    slug: policy.slug,
    name: policy.name,
    description: policy.description ?? '',
    modelIds: policy.models.map((m) => m.id),
    fallback: policy.fallbackPolicyId === null ? NO_FALLBACK : String(policy.fallbackPolicyId),
    thinking: policy.thinking,
    status: policy.status,
    purpose: policy.purpose,
    riskTier: policy.riskTier,
    latencyClass: policy.latencyClass,
    costTier: policy.costTier,
    permissionTier: policy.permissionTier,
    tags: policy.tags ?? [],
    wallClockTimeoutMs: policy.wallClockTimeoutMs,
    providerRequestTimeoutMs: policy.providerRequestTimeoutMs,
    idleTimeoutMs: policy.idleTimeoutMs ?? null,
    maxInputTokens: policy.maxInputTokens ?? null,
    maxOutputTokens: policy.maxOutputTokens,
    maxConcurrentRequests: policy.maxConcurrentRequests ?? null,
    maxSystemPromptChars: policy.maxSystemPromptChars ?? null,
    maxToolCalls: policy.maxToolCalls,
    maxTurns: policy.maxTurns,
    retryMax: policy.retryMax,
    retryBackoffMs: policy.retryBackoffMs,
    retryBackoffMultiplier: policy.retryBackoffMultiplier,
    retryJitter: policy.retryJitter,
    spendCapUsd: policy.spendCapUsd ?? null,
    permissionMode: policy.permissionMode,
    allowedProviderTools: policy.allowedProviderTools ?? [],
    streamingEnabled: policy.streamingEnabled,
    sessionMode: policy.sessionMode,
    promptCachingEnabled: policy.promptCachingEnabled,
    providerExtensions: policy.providerExtensions ?? {},
  };
}

/** The validated form values as the API's upsert body. */
export function formValuesToPayload(values: PolicyFormOutput): ModelRuntimePolicyUpsert {
  const { fallback, ...rest } = values;
  return {
    ...rest,
    slug: rest.slug.trim(),
    name: rest.name.trim(),
    description: rest.description?.trim() || null,
    fallbackPolicyId: fallback === NO_FALLBACK ? null : Number(fallback),
  };
}

// ── Which tab holds which field ─────────────────────────────────────────────

export type PolicyTabId = 'identity' | 'models' | 'routing' | 'limits' | 'permissions' | 'provider' | 'resolve';

/** The tab a form field lives on, so a failed save can say where the problem is. */
export const FIELD_TAB: Record<string, PolicyTabId> = {
  name: 'identity',
  slug: 'identity',
  scope: 'identity',
  status: 'identity',
  description: 'identity',
  modelIds: 'models',
  fallback: 'models',
  thinking: 'models',
  purpose: 'routing',
  riskTier: 'routing',
  latencyClass: 'routing',
  costTier: 'routing',
  permissionTier: 'routing',
  tags: 'routing',
  maxTurns: 'limits',
  maxToolCalls: 'limits',
  maxInputTokens: 'limits',
  maxOutputTokens: 'limits',
  maxConcurrentRequests: 'limits',
  wallClockTimeoutMs: 'limits',
  idleTimeoutMs: 'limits',
  retryMax: 'limits',
  retryBackoffMs: 'limits',
  retryBackoffMultiplier: 'limits',
  retryJitter: 'limits',
  providerRequestTimeoutMs: 'limits',
  spendCapUsd: 'limits',
  permissionMode: 'permissions',
  sessionMode: 'permissions',
  allowedProviderTools: 'permissions',
  streamingEnabled: 'provider',
  promptCachingEnabled: 'provider',
  maxSystemPromptChars: 'provider',
  providerExtensions: 'provider',
};
